// Service Factory - Dependency Injection Container
import { PhantomWalletService } from './implementations/PhantomWalletService.js';
import { UserProfileService } from './implementations/UserProfileService.js';
import { DirectMessageService } from './implementations/DirectMessageService.js';
import { arweaveService } from './ArweaveService.js';
import { getConfig, isFeatureEnabled } from '../config/index.js';

/**
 * Service Factory for Dependency Injection
 * Manages service instances and provides them to components
 * Enables easy testing with mock implementations
 */
class ServiceFactory {
  constructor() {
    this.services = new Map();
    this.config = getConfig();
    this.isInitialized = false;
  }

  /**
   * Initialize the service factory
   * @returns {Promise<void>}
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    if (this.config.ENABLE_DEBUG_LOGS) {
      console.log('🏭 Initializing Service Factory...');
    }

    // Register services based on configuration and feature flags
    await this.registerServices();
    
    this.isInitialized = true;

    if (this.config.ENABLE_DEBUG_LOGS) {
      console.log('✅ Service Factory initialized');
    }
  }

  /**
   * Register all services
   * @private
   */
  async registerServices() {
    // Register wallet service based on feature flags
    if (isFeatureEnabled('ENABLE_NIGHTLY_WALLET')) {
      this.registerWalletService();
    }

    // Register user profile service
    this.registerUserProfileService();

    // Register direct message service
    this.registerDirectMessageService();

    // Register Arweave service for permanent storage
    this.registerArweaveService();

    // Future services can be registered here based on feature flags
    // if (isFeatureEnabled('ENABLE_TOKEN_TIPPING')) {
    //   this.registerTippingService();
    // }
  }

  /**
   * Register wallet service
   * @private
   */
  registerWalletService() {
    const walletService = new PhantomWalletService();
    this.services.set('walletService', walletService);

    if (this.config.ENABLE_DEBUG_LOGS) {
      console.log('👻 Registered Phantom Wallet Service');
    }
  }

  /**
   * Register user profile service
   * @private
   */
  registerUserProfileService() {
    const userProfileService = new UserProfileService();
    this.services.set('userProfileService', userProfileService);

    if (this.config.ENABLE_DEBUG_LOGS) {
      console.log('👤 Registered User Profile Service');
    }
  }

  /**
   * Register direct message service
   * @private
   */
  registerDirectMessageService() {
    // DirectMessage service uses lazy initialization due to runtime dependencies
    // It will be created when first requested via getDirectMessageService()
    this.services.set('directMessageService', null);

    if (this.config.ENABLE_DEBUG_LOGS) {
      console.log('💬 Registered Direct Message Service (lazy initialization)');
    }
  }

  /**
   * Register Arweave service for permanent storage
   * @private
   */
  registerArweaveService() {
    this.services.set('arweaveService', arweaveService);

    if (this.config.ENABLE_DEBUG_LOGS) {
      console.log('🌐 Registered Arweave Service for permanent storage');
    }
  }

  /**
   * Get wallet service instance
   * @returns {IWalletService}
   */
  getWalletService() {
    const service = this.services.get('walletService');
    if (!service) {
      throw new Error('Wallet service not registered. Ensure ENABLE_NIGHTLY_WALLET feature flag is enabled.');
    }
    return service;
  }

  /**
   * Get user profile service instance
   * @returns {IUserProfileService}
   */
  getUserProfileService() {
    const service = this.services.get('userProfileService');
    if (!service) {
      throw new Error('User profile service not registered.');
    }
    return service;
  }

  /**
   * Get Arweave service instance
   * @returns {ArweaveService}
   */
  getArweaveService() {
    const service = this.services.get('arweaveService');
    if (!service) {
      throw new Error('Arweave service not registered.');
    }
    return service;
  }

  /**
   * Get direct message service instance
   * @param {Object} wallet - Optional wallet instance to use
   * @param {Object} connection - Optional connection instance to use
   * @returns {IDirectMessageService}
   */
  getDirectMessageService(wallet = null, connection = null) {
    let service = this.services.get('directMessageService');
    
    // Always recreate service with current wallet/connection if provided
    if (wallet && connection) {
      service = new DirectMessageService(connection, wallet, null);
      this.services.set('directMessageService', service);
      
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('💬 DirectMessage Service updated with current wallet');
      }
    }
    // Lazy initialization - create service when first requested
    else if (service === null) {
      // Create a placeholder service that will be updated when wallet is available
      service = new DirectMessageService(null, null, null);
      this.services.set('directMessageService', service);
      
      if (this.config.ENABLE_DEBUG_LOGS) {
        console.log('💬 DirectMessage Service created (awaiting wallet)');
      }
    }
    
    if (!service) {
      throw new Error('Direct message service not registered.');
    }
    return service;
  }

  /**
   * Register a custom service (for testing or custom implementations)
   * @param {string} serviceName - Name of the service
   * @param {any} serviceInstance - Service instance
   */
  registerService(serviceName, serviceInstance) {
    this.services.set(serviceName, serviceInstance);
    
    if (this.config.ENABLE_DEBUG_LOGS) {
      console.log(`🔧 Registered custom service: ${serviceName}`);
    }
  }

  /**
   * Get a service by name
   * @param {string} serviceName - Name of the service
   * @returns {any} Service instance
   */
  getService(serviceName) {
    // Special handling for DirectMessageService lazy initialization
    if (serviceName === 'directMessageService') {
      return this.getDirectMessageService();
    }
    
    const service = this.services.get(serviceName);
    if (!service) {
      throw new Error(`Service '${serviceName}' not found. Make sure it's registered.`);
    }
    return service;
  }

  /**
   * Check if a service is registered
   * @param {string} serviceName - Name of the service
   * @returns {boolean}
   */
  hasService(serviceName) {
    return this.services.has(serviceName);
  }

  /**
   * Get all registered service names
   * @returns {string[]}
   */
  getRegisteredServices() {
    return Array.from(this.services.keys());
  }

  /**
   * Clear all services (for testing)
   */
  clearServices() {
    this.services.clear();
    this.isInitialized = false;
    
    if (this.config.ENABLE_DEBUG_LOGS) {
      console.log('🧹 Cleared all services');
    }
  }

  /**
   * Reset factory to initial state
   */
  reset() {
    this.clearServices();
    this.config = getConfig();
  }
}

// Create singleton instance
const serviceFactory = new ServiceFactory();

export default serviceFactory;
